import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
	AppBar,
	Box,
	Button,
	Container,
	CssBaseline,
	Link,
	Paper,
	Toolbar,
	Typography,
	useTheme,
} from "@mui/material";
import { useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import ArtemisLogo from "../../assets/images/artemis-headhunting_logo-only-purple.svg?react";
import { useAuth } from "../../context/AuthContext";

export const LandingPage: React.FC = () => {
	const theme = useTheme();
	const { isAuthenticated } = useAuth();
	const navigate = useNavigate();

	// Redirect authenticated users to app
	useEffect(() => {
		if (isAuthenticated) {
			navigate("/todo", { replace: true });
		}
	}, [isAuthenticated, navigate]);

	return (
		<>
			<CssBaseline />
			<Box
				data-component="landing-page"
				sx={{
					minHeight: "100vh",
					display: "flex",
					flexDirection: "column",
					bgcolor: "background.default",
				}}
			>
				{/* Header - matches app navbar style */}
				<AppBar position="static" sx={{ padding: 0 }}>
					<Toolbar>
						<ArtemisLogo
							data-component="landing-logo"
							style={{
								fill: theme.palette.primary.contrastText,
								width: 24,
								height: 24,
								marginRight: theme.spacing(2),
							}}
						/>
						<Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
							Artemis
						</Typography>
						<Box data-component="header-actions">
							<Button
								component={RouterLink}
								to="/login"
								color="inherit"
								sx={{ mr: 1 }}
							>
								Sign In
							</Button>
							<Button
								component={RouterLink}
								to="/register"
								variant="outlined"
								color="inherit"
							>
								Get Started
							</Button>
						</Box>
					</Toolbar>
				</AppBar>

				{/* Hero Section */}
				<Container maxWidth="md" sx={{ flex: 1, py: 8 }}>
					<Box
						data-component="hero-section"
						sx={{ textAlign: "center", mb: 8 }}
					>
						<Typography
							variant="h2"
							component="h1"
							gutterBottom
							sx={{
								fontWeight: 700,
								mb: 3,
								// h2 is 3.75rem, at which "Recruitment" alone is
								// 345px wide — 13px under the content box of a
								// 390px screen, so the page's whole min-width
								// hangs on one word's metrics and a font that
								// renders it slightly wider pushes the document
								// sideways. Smaller below md leaves real room.
								fontSize: { xs: "2.25rem", md: "3.75rem" },
							}}
						>
							Streamline Your Recruitment Process
						</Typography>
						<Typography
							variant="h5"
							color="text.secondary"
							sx={{ mb: 4, lineHeight: 1.6 }}
						>
							A powerful platform designed to help teams discover,
							evaluate, and manage candidates throughout the entire hiring
							journey.
						</Typography>
						<Box data-component="cta-buttons">
							<Button
								component={RouterLink}
								to="/register"
								variant="contained"
								size="large"
								sx={{ mr: 2, px: 4, py: 1.5 }}
							>
								Get Started
							</Button>
							<Button
								component={RouterLink}
								to="/login"
								variant="outlined"
								size="large"
								sx={{ px: 4, py: 1.5 }}
							>
								Sign In
							</Button>
						</Box>
					</Box>

					{/* Features Section */}
					<Box data-component="features-section" sx={{ mb: 8 }}>
						<Typography
							variant="h4"
							component="h2"
							align="center"
							gutterBottom
							sx={{ mb: 4, fontWeight: 600 }}
						>
							Key Features
						</Typography>
						<Box
							data-component="features-grid"
							sx={{
								display: "grid",
								gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
								gap: 3,
							}}
						>
							<Paper
								data-component="feature-card"
								elevation={1}
								sx={{ p: 3, display: "flex", gap: 2 }}
							>
								<CheckCircleIcon color="primary" sx={{ mt: 0.5 }} />
								<Box>
									<Typography variant="h6" gutterBottom>
										Candidate Discovery
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Search and discover qualified candidates from
										multiple professional networks and sources, all
										in one place.
									</Typography>
								</Box>
							</Paper>

							<Paper
								data-component="feature-card"
								elevation={1}
								sx={{ p: 3, display: "flex", gap: 2 }}
							>
								<CheckCircleIcon color="primary" sx={{ mt: 0.5 }} />
								<Box>
									<Typography variant="h6" gutterBottom>
										Pipeline Management
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Organize recruitment projects, track candidate
										progress through customizable stages, and
										collaborate seamlessly with your team.
									</Typography>
								</Box>
							</Paper>

							<Paper
								data-component="feature-card"
								elevation={1}
								sx={{ p: 3, display: "flex", gap: 2 }}
							>
								<CheckCircleIcon color="primary" sx={{ mt: 0.5 }} />
								<Box>
									<Typography variant="h6" gutterBottom>
										Unified Communication
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Manage all candidate communications through
										multiple channels in one centralized platform
										with complete tracking.
									</Typography>
								</Box>
							</Paper>

							<Paper
								data-component="feature-card"
								elevation={1}
								sx={{ p: 3, display: "flex", gap: 2 }}
							>
								<CheckCircleIcon color="primary" sx={{ mt: 0.5 }} />
								<Box>
									<Typography variant="h6" gutterBottom>
										AI-Powered Matching
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Leverage machine learning to automatically score
										and rank candidates based on your requirements,
										improving match quality and saving time.
									</Typography>
								</Box>
							</Paper>
						</Box>
					</Box>

					{/* Data & Privacy Section */}
					<Box
						data-component="privacy-section"
						sx={{ mb: 8, textAlign: "center" }}
					>
						<Typography
							variant="body1"
							color="text.secondary"
							component="p"
							sx={{ mb: 2 }}
						>
							We take data privacy seriously. For complete details about
							how we collect, use, and protect your information, please
							review our{" "}
							<Link
								component={RouterLink}
								to="/privacy-policy"
								sx={{ fontWeight: 600 }}
							>
								Privacy Policy
							</Link>
							.
						</Typography>
					</Box>
				</Container>

				{/* Footer */}
				<Box
					data-component="landing-footer"
					sx={{
						py: 3,
						px: 3,
						borderTop: 1,
						borderColor: "divider",
						bgcolor: "background.paper",
					}}
				>
					<Container maxWidth="md">
						<Box
							data-component="footer-content"
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: 2,
							}}
						>
							<Typography variant="body2" color="text.secondary">
								© {new Date().getFullYear()} Artemis Recruitment
								Platform. All rights reserved.
							</Typography>
							<Box
								data-component="footer-links"
								sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}
							>
								<Link
									component={RouterLink}
									to="/privacy-policy"
									variant="body2"
									sx={{ textDecoration: "none" }}
								>
									Privacy Policy
								</Link>
								<Link
									component={RouterLink}
									to="/terms-of-service"
									variant="body2"
									sx={{ textDecoration: "none" }}
								>
									Terms of Service
								</Link>
								<Link
									href="mailto:admin@artemisrec.com"
									variant="body2"
									sx={{ textDecoration: "none" }}
								>
									Contact
								</Link>
							</Box>
						</Box>
					</Container>
				</Box>
			</Box>
		</>
	);
};
